import { Description } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { KIND as ButtonKind } from 'baseui/button';
import {
  Modal,
  ModalBody,
  ModalButton,
  ModalFooter,
  ModalHeader,
} from 'baseui/modal';
import { Spinner } from 'baseui/spinner';
import Text from 'components/Text';
import React, { useState } from 'react';
import { getOpOrdDoc } from 'services/api/inspection/queries';
import { fetcherBlob } from 'services/fetcher';
import { useStitches } from 'theme';
import { isAPIError } from 'utils/error';

// Every .docx is a ZIP archive, which starts with the "PK\x03\x04" local-file
// header. The opOrd endpoint returns a link (and a 200 response) even for
// inspections with no uploaded file — that link resolves to empty or non-docx
// content — so "a link exists" and "the download succeeded" are both
// unreliable signals. Checking the magic bytes is what actually tells us a real
// document is there.
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];

const isDocxBlob = async (blob: Blob) => {
  if (blob.size < ZIP_MAGIC.length) {
    return false;
  }
  const header = new Uint8Array(
    await blob.slice(0, ZIP_MAGIC.length).arrayBuffer(),
  );
  return (
    header.length === ZIP_MAGIC.length &&
    ZIP_MAGIC.every((byte, index) => header[index] === byte)
  );
};

// Eagerly resolve + validate the inspection's OpORD document so the preview
// button can reflect real availability. Downloads the file once to confirm it's
// a real .docx (the opOrd endpoint returns a link even when no file exists); the
// modal itself renders from the link via the Office Online viewer.
const useOpordDocument = (inspectionId: string) =>
  useQuery({
    queryKey: ['opord', inspectionId, 'document'],
    queryFn: async () => {
      let link: string | undefined;
      try {
        ({ link } = await getOpOrdDoc({
          params: { inspectionUUID: inspectionId },
        }));
      } catch (error) {
        // A 404 just means no document exists — an empty state, not an error.
        if (isAPIError(error) && error.code === 404) {
          return { hasDocument: false as const };
        }
        throw error;
      }

      if (!link) {
        return { hasDocument: false as const };
      }

      let blob: Blob;
      try {
        blob = await fetcherBlob(link);
      } catch (error) {
        // The link is present but the file isn't actually there.
        if (isAPIError(error) && error.code === 404) {
          return { hasDocument: false as const };
        }
        throw error;
      }

      // A link with empty / non-docx content means there's no real document.
      if (!(await isDocxBlob(blob))) {
        return { hasDocument: false as const };
      }

      return { hasDocument: true as const, blob, link };
    },
    enabled: !!inspectionId,
    staleTime: Infinity,
  });

export default function PreviewInspectionDocument({
  inspectionId,
}: {
  inspectionId: string;
}) {
  const { css, theme } = useStitches();
  const [isOpen, setIsOpen] = useState(false);

  const docQuery = useOpordDocument(inspectionId);
  const data = docQuery.data;
  const hasDocument = !!data?.hasDocument;
  // Treat "settled without a valid document" (no link, 404, or non-docx
  // content) as "no document" so the button greys out instead of opening an
  // empty preview.
  const noDocument = !docQuery.isLoading && !hasDocument;
  const link = data?.hasDocument ? data.link : undefined;

  if (!inspectionId) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={
          noDocument
            ? 'No Inspection Document for this inspection'
            : 'Preview Inspection Document'
        }
      >
        <Description
          onClick={() => {
            if (hasDocument) {
              setIsOpen(true);
            }
          }}
          // Grey the icon out when there's no document. Use an inline style
          // (not a stitches class) so the disabled colour reliably wins over
          // MUI's own SvgIcon `fill: currentColor` rule — a class can lose that
          // specificity battle, which left the icon looking fully enabled.
          style={
            noDocument
              ? { color: '#9e9e9e', fill: '#9e9e9e', cursor: 'not-allowed' }
              : undefined
          }
          className={
            noDocument
              ? undefined
              : css({ cursor: 'pointer', backgroundColor: '$primary' })
          }
        />
      </Tooltip>
      <Modal
        onClose={({ closeSource }) =>
          closeSource === 'closeButton' ? setIsOpen(false) : null
        }
        isOpen={isOpen}
        overrides={{
          DialogContainer: {
            style: {
              alignItems: 'center',
            },
          },
          Dialog: {
            style: {
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              height: '80vh',
              width: '80vw',
              marginLeft: theme.sizing.scale600,
              marginRight: theme.sizing.scale600,
              marginBottom: theme.sizing.scale600,
              borderBottomLeftRadius: theme.sizing.scale600,
              borderBottomRightRadius: theme.sizing.scale600,
            },
          },
        }}
      >
        <ModalHeader>Preview Document</ModalHeader>
        <ModalBody
          className={css({
            layout: 'stack',
            flex: '1 1 0',
            overflowY: 'hidden',
          })}
        >
          {link ? (
            <div
              className={css({
                layout: 'stack',
                overflow: 'hidden',
                spaceY: '$scale600',
                flex: 1,
                minHeight: 0,
              })}
            >
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className={css({
                  ':hover': { color: '$primary' },
                })}
              >
                <Text>Download File</Text>
              </a>
              {/* Render with Microsoft's Office Online viewer instead of a
                  client-side DOCX->HTML converter. The viewer is real Word
                  rendering, so it reproduces any document's layout (complex
                  merged tables, shading, images) faithfully — no per-document
                  CSS tuning. Note: the document URL is sent to Microsoft, who
                  fetch and render the file on their servers, so the URL must be
                  publicly reachable (the signed GCS links on dev/prod are).
                  "Download File" above stays as the always-available fallback. */}
              <iframe
                title="Inspection document preview"
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                  link,
                )}`}
                className={css({
                  border: 'none',
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                })}
              />
            </div>
          ) : (
            <div
              className={css({
                display: 'flex',
                justifyContent: 'center',
                p: '$scale600',
                flex: 1,
              })}
            >
              <Spinner />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <ModalButton
            kind={ButtonKind.tertiary}
            onClick={() => setIsOpen(false)}
          >
            Close
          </ModalButton>
        </ModalFooter>
      </Modal>
    </>
  );
}
